import { CanReadOrderExceptions, HasPerm, HasRole, Session } from 'smg/admin/session';
import * as models from 'smg/admin/models';
import { IsFile, Relref } from './hugo';
import i18n from './i18n';

declare interface myWindow extends Window {
    readonly IAPV2Root: string;
}
declare const window: myWindow;

export interface Link {
    group?: string;
    name: string;
    icons: string[];
    url: string;
    badge?: string;
    // external=true opens the link in a new tab and appends an external-link
    // icon. Set this when the route ultimately leaves the site (e.g. /statistics
    // server-redirects to kpis.gg).
    external?: boolean;
}

export async function Directory(): Promise<Link[]> {
    const sess = await Session();

    const links: Link[] = [];

    if (!await HasRole(models.AdminRoleID.ADMIN_ROLES_FACTORY)) {
        // Dev-only until the designs rollout (#6721) is announced.
        if (await HasPerm('r/designs') && await HasRole(models.AdminRoleID.ADMIN_ROLES_DEVELOPER)) {
            links.push({
                group: '',
                name: i18n('navDesigns'),
                icons: ['fas fa-palette'],
                url: Relref('/designs'),
            });
        }

        if (await HasPerm('r/users')) {
            links.push({
                group: '',
                name: i18n('navCustomers'),
                icons: ['fas fa-user'],
                url: Relref('/customers'),
            });
        }

        if (await HasPerm('r/quotes')) {
            const defaultQuotesPage = sess.user.userDetails?.iapDefaultQuotesPage?.replace(/\/+$/, '') || '';
            links.push({
                name: i18n('navQuotes'),
                icons: ['fas fa-quote-left'],
                url: defaultQuotesPage && IsFile(defaultQuotesPage) ? Relref(defaultQuotesPage) : Relref('/quotes'),
            });

            links.push({
                name: i18n('navUnsentQuotes'),
                icons: ['fas fa-quote-left', 'fas fa-paper-plane'],
                url: sess.user.userDetails?.iapDefaultQuotesPage === '/quotes/projects/' ? Relref('/quotes/projects/unsent') : Relref('/quotes/unsent'),
                badge: 'unsentQuoteRequests',
            });
        }

        if (await HasPerm('r/followUps')) {
            links.push({
                name: i18n('navFollowUps'),
                icons: ['fas fa-reply-all'],
                url: Relref('/follow-ups'),
            });
        }

        if (await HasPerm('r/orders?pending=true&preorder=false')) {
            links.push({
                name: i18n('navPending'),
                icons: ['fas fa-hourglass-start'],
                url: Relref('/orders/pending'),
                badge: 'pendingOrders',
            });
        }

        if (await HasPerm('r/orders')) {
            links.push({
                name: i18n('navOrders'),
                icons: ['fas fa-check-circle'],
                url: Relref('/orders'),
            });
        }

        // ==================== INBOX ====================

        if (await HasPerm('r/groupChats')) {
            links.push({
                group: i18n('navGroupInbox'),
                name: i18n('navInbox'),
                icons: ['fas fa-inbox'],
                url: Relref('/inbox'),
                badge: 'inbox',
            });

            links.push({
                group: i18n('navGroupInbox'),
                name: i18n('navInboxClosed'),
                icons: ['fas fa-inbox', 'fas fa-check'],
                url: Relref('/inbox/closed'),
            });

            links.push({
                group: i18n('navGroupInbox'),
                name: i18n('navSent'),
                icons: ['fas fa-inbox', 'fas fa-paper-plane'],
                url: Relref('/inbox/sent'),
            });
        }

        if (await HasPerm('w/groupChatMessages/approved')) {
            links.push({
                group: i18n('navGroupInbox'),
                name: i18n('navNeedsApproval'),
                icons: ['fas fa-inbox', 'fas fa-exclamation'],
                url: Relref('/inbox/needs-approval'),
                badge: 'unapprovedInbox',
            });
        }

        // ==================== ART ====================

        if (await HasPerm('r/quoteRequestProjectTasks')) {
            links.push({
                group: i18n('navGroupArt'),
                name: i18n('navFriday'),
                icons: ['fab fa-fort-awesome'],
                url: Relref('/friday'),
                badge: 'friday',
            });
        }

        if (await HasRole(models.AdminRoleID.ADMIN_ROLES_SALES)) {
            if (await HasPerm('r/quoteRequestProjectTasks')) {
                links.push({
                    group: i18n('navGroupArt'),
                    name: i18n('navReviewNeeded'),
                    icons: ['fab fa-fort-awesome', 'fas fa-exclamation'],
                    url: Relref('/friday/review-needed'),
                    badge: 'fridayReviewNeeded',
                });
            }

            if (await HasPerm('r/quoteRequestProjectTasks')) {
                links.push({
                    group: i18n('navGroupArt'),
                    name: i18n('navArtDone'),
                    icons: ['fab fa-fort-awesome', 'fas fa-paint-brush'],
                    url: Relref('/friday/art-done'),
                    badge: 'fridayArtDone',
                });
            }

            if (await HasPerm('r/quoteRequestProjectTasks')) {
                links.push({
                    group: i18n('navGroupArt'),
                    name: i18n('navSentToday'),
                    icons: ['fab fa-fort-awesome', 'fas fa-paper-plane'],
                    url: Relref('/friday/sent-today'),
                });
            }
        }

        if (await HasPerm('r/orders/proofs') || await HasPerm('r/orders')) {
            links.push({
                group: i18n('navGroupArt'),
                name: i18n('navProofsOrderedToday'),
                icons: ['fas fa-image', 'fas fa-check-circle'],
                url: Relref('/proofs-ordered-today'),
            });
        }

        // ==================== ORDERS ====================

        if (await HasPerm('r/orders/shipments')) {
            links.push({
                group: i18n('navGroupOrders'),
                name: i18n('navLateOrders'),
                icons: ['fas fa-clock', 'fas fa-exclamation'],
                url: Relref('/orders/late'),
                badge: 'late',
            });
        }

        if (await HasPerm('r/orders/shipments') && await CanReadOrderExceptions()) {
            links.push({
                group: i18n('navGroupOrders'),
                name: i18n('navLateRisk'),
                icons: ['fas fa-clock', 'fas fa-exclamation-triangle'],
                url: Relref('/orders/late-risk'),
            });
        }

        if (await HasPerm('r/orders')) {
            links.push({
                group: i18n('navGroupOrders'),
                name: i18n('navPreorders'),
                icons: ['fas fa-shopping-basket'],
                url: Relref('/orders/preorders'),
            });
        }

        if (await HasPerm('r/orders/sampleRequest')) {
            links.push({
                group: i18n('navGroupOrders'),
                name: i18n('navSampleRequests'),
                icons: ['fas fa-puzzle-piece'],
                url: Relref('/orders/samples'),
            });

            // Marketing-only pre-filtered sample views (#582): the photo team's
            // received-but-not-yet-photographed queue and the photographed set.
            // Everyone else just gets the main Sample Requests queue above.
            if (await HasRole(models.AdminRoleID.ADMIN_ROLES_MARKETING)) {
                links.push({
                    group: i18n('navGroupOrders'),
                    name: i18n('navSamplesReceivedNotPhotographed'),
                    icons: ['fas fa-box-open'],
                    url: Relref('/orders/samples/received-not-photographed'),
                }, {
                    group: i18n('navGroupOrders'),
                    name: i18n('navSamplesPhotographed'),
                    icons: ['fas fa-camera'],
                    url: Relref('/orders/samples/photographed'),
                });
            }
        }

        if (await HasPerm('r/orders/payments')) {
            links.push({
                group: i18n('navGroupOrders'),
                name: i18n('navUnpaidOrders'),
                icons: ['fas fa-fire'],
                url: Relref('/orders/unpaid'),
                badge: 'unpaidOrders',
            });
        }

        if (await HasPerm('r/orders?orderStatus=order')) {
            links.push({
                group: i18n('navGroupOrders'),
                name: i18n('navUnplaced'),
                icons: ['fas fa-inbox'],
                url: Relref('/orders/unplaced'),
                badge: 'unplaced',
            });
        }

        if (await HasPerm('r/orders?orderStatus=unconfirmed')) {
            links.push({
                group: i18n('navGroupOrders'),
                name: i18n('navUnconfirmed'),
                icons: ['fas fa-asterisk'],
                url: Relref('/orders/unconfirmed'),
                badge: 'unconfirmed',
            });
        }

        if (await HasPerm('r/orders/shipments')) {
            links.push({
                group: i18n('navGroupOrders'),
                name: i18n('navUnshippedOrders'),
                icons: ['fas fa-plane'],
                url: Relref('/orders/unshipped'),
                badge: 'unshipped',
            });
        }

        if (await HasPerm('r/shippedOrders')) {
            links.push({
                group: i18n('navGroupOrders'),
                name: i18n('navShippedOrders'),
                icons: ['fas fa-check'],
                url: Relref('/orders/shipped'),
            });
        }

        if (await HasPerm('r/orders/shipments')) {
            links.push({
                name: i18n('navLateHistory'),
                group: i18n('navGroupOrders'),
                icons: ['fas fa-clock', 'fas fa-dollar-sign'],
                url: Relref('/orders/late-history'),
            });
        }

        // ==================== SHIPPING ====================

        if (await HasPerm('r/orders/shipments')) {
            links.push({
                group: i18n('navGroupShipping'),
                name: i18n('navShipments'),
                icons: ['fas fa-truck'],
                url: Relref('/shipments'),
            });
        }

        if (await HasPerm('r/rates')) {
            links.push({
                group: i18n('navGroupShipping'),
                name: i18n('navShippingRates'),
                icons: ['fas fa-dollar-sign', 'fas fa-shipping-fast'],
                url: Relref('/rates'),
            });
        }

        if (await HasPerm('w/orders/shipments')) {
            links.push({
                group: i18n('navGroupShipping'),
                name: i18n('navShippedToBuilding'),
                icons: ['fas fa-bomb'],
                url: Relref('/orders/shipped-to-building'),
            });
        }

        if (await HasPerm('r/shippingInvoiceFiles')) {
            links.push({
                group: i18n('navGroupShipping'),
                name: i18n('navShippingInvoices'),
                icons: ['fas fa-folder-open'],
                url: Relref('/shipping-invoice-files'),
            });
        }

        if (await HasPerm('r/shippingInvoiceFiles')) {
            links.push({
                group: i18n('navGroupShipping'),
                name: i18n('navUnpairedBills'),
                icons: ['fas fa-question-circle'],
                url: Relref('/shipping-bills/unpaired'),
                badge: 'unpairedBills',
            });
        }

        // Inverse of Unpaired Bills: shipments carrying cost nobody vouched for
        // and no carrier bill backs (#7553). Accounting-only — commissions gate.
        if (await HasPerm('r/shipments/cost/affectsCommissions')) {
            links.push({
                group: i18n('navGroupShipping'),
                name: i18n('navUnpairedShipments'),
                icons: ['fas fa-truck', 'fas fa-question-circle'],
                url: Relref('/shipments/unpaired'),
                badge: 'unpairedShipments',
            });
        }

        // Deleted shipments whose carrier void failed — the label is still live
        // and may be billed (#8099). Queue drains via Confirm Cost once the label
        // is reconciled against the carrier invoice.
        if (await HasPerm('r/shipments/cost/affectsCommissions')) {
            links.push({
                group: i18n('navGroupShipping'),
                name: i18n('navUnvoidedLabels'),
                icons: ['fas fa-truck', 'fas fa-ban'],
                url: Relref('/shipments/unvoided'),
                badge: 'unvoidedLabels',
            });
        }

        // Labels the ShipEngine label audit stamped as errored postage (#10496) with
        // no delivery and no person's verdict (#9251). The queue drains through the
        // row's Resolve dialog (#10476). Same gate as the Shipments list: a rep
        // sees the page narrowed to their own shipments by the hyejoo. The badge is
        // the whole queue's size, so it goes to the roles that work the queue.
        if (await HasPerm('r/orders/shipments')) {
            const link: Link = {
                group: i18n('navGroupShipping'),
                name: i18n('navErroredPostage'),
                icons: ['fas fa-truck', 'fas fa-exclamation-triangle'],
                url: Relref('/shipments/errored-postage'),
            };
            const worksQueue = await HasRole(models.AdminRoleID.ADMIN_ROLES_SMG_PACKING_AND_SHIPPING) ||
                await HasRole(models.AdminRoleID.ADMIN_ROLES_ACCOUNTING) ||
                await HasRole(models.AdminRoleID.ADMIN_ROLES_SALES);
            if (worksQueue) {
                link.badge = 'erroredPostage';
            }
            links.push(link);
        }

        // Gated on the review flag's own generated read perm (not the broad
        // shipping-costs perm) — the audit queue is accounting/admin work, not
        // something plain sales should see or action (#7914).
        if (await HasPerm('r/flags/accounting/doublePayShippingReviewed')) {
            links.push({
                group: i18n('navGroupShipping'),
                name: i18n('navDoublePaidShipping'),
                icons: ['fas fa-truck', 'fas fa-dollar-sign'],
                url: Relref('/orders/double-paid-shipping'),
                badge: 'doublePaidShipping',
            });
        }

        // ==================== FACTORIES ===================

        if (await HasPerm('r/blanks')) {
            links.push({
                group: i18n('navGroupManufacturing'),
                name: i18n('navBlanks'),
                icons: ['fas fa-coins'],
                url: Relref('/blanks'),
            });
        }

        // ==================== FACTORIES ===================

        if (await HasPerm('r/userOrderBids')) {
            links.push({
                group: i18n('navGroupFactories'),
                name: i18n('navIncomingBids'),
                icons: ['fas fa-shopping-cart'],
                url: Relref('/orders/bids/incoming'),
                badge: 'bids',
            });
        }

        if (await HasPerm('r/userOrderBids')) {
            links.push({
                group: i18n('navGroupFactories'),
                name: i18n('navBiddingHistory'),
                icons: ['fas fa-history'],
                url: Relref('/orders/bids'),
            });
        }

        if (await HasPerm('w/factories')) {
            links.push({
                group: i18n('navGroupFactories'),
                name: i18n('navFactories'),
                icons: ['fas fa-industry'],
                url: Relref('/factories'),
            });
        }

        if (await HasPerm('r/orders/factoryCost')) {
            links.push({
                group: i18n('navGroupFactories'),
                name: i18n('navFactoryBills'),
                icons: ['fas fa-paperclip'],
                url: Relref('/orders/factory-bills'),
            });
        }

        // r/factoryCosts, not r/orders/factoryCost, which is a misspelled
        // duplicate of this gate.
        if (await HasPerm('r/factoryCosts')) {
            links.push({
                group: i18n('navGroupFactories'),
                name: i18n('navFactoryCosts'),
                icons: ['fas fa-industry'],
                url: Relref('/orders/factory-costs'),
                badge: 'unapprovedFactoryCosts',
            });
        }

        // ==================== STATS ====================

        if (await HasPerm('r/statistics')) {
            links.push({
                group: i18n('navGroupStats'),
                name: i18n('navStatistics'),
                icons: ['fas fa-chart-pie'],
                url: `${window.IAPV2Root}/statistics`,
                external: true,
            });
        }

        if (await HasPerm('r/statistics')) {
            links.push({
                group: i18n('navGroupStats'),
                name: i18n('navSuperStats'),
                icons: ['fas fa-id-badge'],
                url: `${window.IAPV2Root}/superstats`,
            });
        }

        if (await HasPerm('r/statistics')) {
            links.push(
                {
                    group: i18n('navGroupStats'),
                    name: i18n('navCompanyGrowthStats'),
                    icons: ['fas fa-chart-line'],
                    url: 'https://kpis.gg/d/f6063c75-bfb7-42f9-b39e-46f8c9a61326/company-growth-stats?orgId=1',
                    external: true,
                },
                {
                    group: i18n('navGroupStats'),
                    name: i18n('navGoals'),
                    icons: ['fas fa-bullseye'],
                    url: 'https://kpis.gg/d/eeer98x1zltkwb/goals?orgId=1',
                    external: true,
                },
                {
                    group: i18n('navGroupStats'),
                    name: i18n('navConversionsByTeam'),
                    icons: ['fas fa-exchange-alt'],
                    url: 'https://kpis.gg/d/sales-conversions-by-team/conversions-by-team?orgId=1',
                    external: true,
                },
                {
                    group: i18n('navGroupStats'),
                    name: i18n('navCustomerBaseByTeam'),
                    icons: ['fas fa-users'],
                    url: 'https://kpis.gg/d/sales-customer-base-by-team/current-customer-base-by-sales-team?orgId=1',
                    external: true,
                },
                {
                    group: i18n('navGroupStats'),
                    name: i18n('navSalesLeaderboard'),
                    icons: ['fas fa-trophy'],
                    url: 'https://kpis.gg/d/sales-leaderboard/sales-leaderboard?orgId=1',
                    external: true,
                },
            );
        }

        // ==================== EMAIL ====================

        if (await HasPerm('r/emails')) {
            links.push({
                group: i18n('navGroupEmail'),
                name: i18n('navEmailBlasts'),
                icons: ['fas fa-reply'],
                url: Relref('/email-blasts'),
            });
        }

        if (await HasPerm('r/emailMessageTemplates')) {
            links.push({
                group: i18n('navGroupEmail'),
                name: i18n('navEmailTemplates'),
                icons: ['fas fa-th-large'],
                url: Relref('/emails/templates'),
            });
        }

        if (await HasPerm('r/emails')) {
            links.push({
                group: i18n('navGroupEmail'),
                name: i18n('navEmails'),
                icons: ['fas fa-inbox'],
                url: Relref('/emails'),
            });
        }

        // ==================== TEXTING ====================

        if (await HasPerm('r/textMessages')) {
            links.push({
                group: i18n('navGroupTexting'),
                name: i18n('navTexting'),
                icons: ['fas fa-comments'],
                url: Relref('/texting'),
                badge: 'myOpenTextConversations',
            });
        }

        if (await HasPerm('r/textTemplates')) {
            links.push({
                group: i18n('navGroupTexting'),
                name: i18n('navTextTemplates'),
                icons: ['fas fa-square', 'fas fa-sms'],
                url: Relref('/texting/templates'),
            });
        }

        // ==================== MARKETING ====================

        if (await HasPerm('r/links')) {
            links.push({
                group: i18n('navGroupMarketing'),
                name: i18n('navCampaigns'),
                icons: ['fas fa-link'],
                url: Relref('/campaigns'),
            });
        }

        if (await HasPerm('r/images')) {
            links.push({
                group: i18n('navGroupMarketing'),
                name: i18n('navImages'),
                icons: ['fas fa-images'],
                url: Relref('/images'),
            });
        }

        if (await HasPerm('r/pageRequests')) {
            links.push({
                group: i18n('navGroupMarketing'),
                name: i18n('navPageRequests'),
                icons: ['fas fa-frog'],
                url: Relref('/page-requests'),
            });
        }

        if (await HasPerm('r/promotionalCodes')) {
            links.push({
                group: i18n('navGroupMarketing'),
                name: i18n('navPromoCodes'),
                icons: ['fas fa-tag'],
                url: `${window.IAPV2Root}/promos`,
            });
        }

        if (!await HasRole(models.AdminRoleID.ADMIN_ROLES_SALES) && await HasPerm('r/quotes')) {
            links.push({
                group: i18n('navGroupMarketing'),
                name: i18n('navQuoteRequests'),
                icons: ['fas fa-quote-left', 'fas fa-clipboard-list'],
                url: Relref('/quotes/requests'),
            });
        }

        if (await HasPerm('r/formSubmissions')) {
            links.push({
                group: i18n('navGroupMarketing'),
                name: i18n('navFormSubmissions'),
                icons: ['fas fa-file-signature'],
                url: Relref('/form-submissions'),
            });
        }

        if (await HasPerm('r/uploads') || await HasPerm('r/images')) {
            links.push({
                group: i18n('navGroupMarketing'),
                name: i18n('navImageSearch'),
                icons: ['fas fa-photo-video', 'fas fa-search'],
                url: Relref('/search/images'),
            });
        }

        if (await HasPerm('r/uploads')) {
            links.push({
                group: i18n('navGroupMarketing'),
                name: i18n('navUploadedFiles'),
                icons: ['fas fa-photo-video'],
                url: Relref('/uploads'),
            });
        }

        // ==================== CONTENT =================

        if (await HasPerm('w/artworkGroups')) {
            links.push({
                group: i18n('navGroupContent'),
                name: i18n('navArtworkGroups'),
                icons: ['fas fa-cloud-upload-alt'],
                url: `${window.IAPV2Root}/artworkgroups`,
            });
        }

        if (await HasPerm('w/blogPosts')) {
            links.push({
                group: i18n('navGroupContent'),
                name: i18n('navBlogPosts'),
                icons: ['fas fa-rss'],
                url: Relref('/blog-posts'),
            });
        }

        if (await HasPerm('w/galleries')) {
            links.push({
                group: i18n('navGroupContent'),
                name: i18n('navGalleries'),
                icons: ['fas fa-images'],
                url: Relref('/galleries'),
            });
        }

        // ==================== COMMISSIONS ====================

        if (await HasPerm('r/commissions')) {
            links.push({
                group: i18n('navGroupCommissions'),
                name: i18n('navCommissions'),
                icons: ['fas fa-child'],
                url: Relref('/commissions'),
            });
        }
        if (await HasPerm('r/commissions')) {
            links.push({
                group: i18n('navGroupCommissions'),
                name: i18n('navCommissionTransfers'),
                icons: ['fas fa-exchange-alt'],
                url: Relref('/commission-transfers'),
            });
        }

        // ==================== LINE ITEMS ====================

        if (await HasPerm('w/lineItems')) {
            links.push({
                group: i18n('navGroupLineItems'),
                name: i18n('navLineItems'),
                icons: ['fas fa-microchip'],
                url: `${window.IAPV2Root}/lineitems`,
            });
        }

        if (await HasPerm('r/stages')) {
            links.push({
                group: i18n('navGroupLineItems'),
                name: i18n('navStages'),
                icons: ['fas fa-layer-group'],
                url: Relref('/stages'),
            });
        }

        if (await HasPerm('r/lineItemStages')) {
            links.push({
                group: i18n('navGroupLineItems'),
                name: i18n('navLineItemStages'),
                icons: ['fas fa-coffee'],
                url: Relref('/line-item-stages'),
            });
        }

        if (await HasPerm('r/orderLineItems')) {
            links.push({
                group: i18n('navGroupLineItems'),
                name: i18n('navOrderLineItems'),
                icons: ['fas fa-beer'],
                url: Relref('/orders/line-items'),
            });
        }

        if (await HasPerm('w/networkSkus')) {
            links.push({
                group: i18n('navGroupLineItems'),
                name: i18n('navNetworkSkus'),
                icons: ['fas fa-barcode'],
                url: Relref('/network-skus'),
            });
        }

        if (await HasPerm('r/lineItems')) {
            links.push({
                group: i18n('navGroupLineItems'),
                name: i18n('navDesignPricing'),
                icons: ['fas fa-chart-line'],
                url: Relref('/design-pricing'),
            });
        }

        // Dev-only until the molds rollout (#6721) is announced.
        if (await HasPerm('r/orders') && await HasRole(models.AdminRoleID.ADMIN_ROLES_DEVELOPER)) {
            links.push({
                group: i18n('navGroupLineItems'),
                name: i18n('navMolds'),
                icons: ['fas fa-cube'],
                url: Relref('/molds'),
            });
        }

        if (await HasPerm('r/lineItems') && await HasRole(models.AdminRoleID.ADMIN_ROLES_DEVELOPER)) {
            links.push({
                group: i18n('navGroupLineItems'),
                name: i18n('navNetworkMoldPolicies'),
                icons: ['fas fa-sliders-h'],
                url: Relref('/network-mold-policies'),
            });
        }

        // ==================== Admin ====================

        if (await HasPerm('w/admins')) {
            links.push({
                group: i18n('navGroupAdmin'),
                name: i18n('navAdmins'),
                icons: ['fas fa-lock'],
                url: `${window.IAPV2Root}/admins`,
            });
        }

        if (await HasPerm('w/adminRoles')) {
            links.push({
                group: i18n('navGroupAdmin'),
                name: i18n('navAdminRoles'),
                icons: ['fas fa-user', 'fas fa-lock'],
                url: Relref('/admin-roles'),
            });
        }

        if (await HasPerm('w/realms')) {
            links.push({
                group: i18n('navGroupAdmin'),
                name: i18n('navCompanies'),
                icons: ['fas fa-globe'],
                url: `${window.IAPV2Root}/companies`,
            });
        }

        if (await HasPerm('r/realms') && await HasRole(models.AdminRoleID.ADMIN_ROLES_DEVELOPER)) {
            links.push({
                group: i18n('navGroupAdmin'),
                name: i18n('navRealms'),
                icons: ['fas fa-globe'],
                url: Relref('/realms'),
            });
        }

        if (await HasPerm('w/invoiceTemplates')) {
            links.push({
                group: i18n('navGroupAdmin'),
                name: i18n('navInvoiceTemplates'),
                icons: ['far fa-credit-card'],
                url: Relref('/invoice-templates'),
            });
        }

        if (await HasPerm('w/managingCompanies')) {
            links.push({
                group: i18n('navGroupAdmin'),
                name: i18n('navManagingCompanies'),
                icons: ['fas fa-compass'],
                url: `${window.IAPV2Root}/managingcompanies`,
            });
        }

        if (await HasPerm('w/networks')) {
            links.push({
                group: i18n('navGroupAdmin'),
                name: i18n('navNetworks'),
                icons: ['fas fa-sitemap'],
                url: `${window.IAPV2Root}/networks`,
            });
        }

        if (await HasPerm('r/orderPayments')) {
            links.push({
                group: i18n('navGroupAdmin'),
                name: i18n('navOrderPayments'),
                icons: ['fas fa-dollar-sign'],
                url: Relref('/orders/payments'),
            });
        }

        if (await HasPerm('r/qboDeposited')) {
            links.push({
                group: i18n('navGroupAdmin'),
                name: i18n('navQboDeposited'),
                icons: ['fas fa-sync'],
                url: Relref('/orders/payments/qbo-deposited'),
                badge: 'qboDeposited',
            });
        }

        if (await HasPerm('w/salesTeams')) {
            links.push({
                group: i18n('navGroupAdmin'),
                name: i18n('navSalesTeams'),
                icons: ['fas fa-users'],
                url: Relref('/sales-teams'),
            });
        }

        if (await HasPerm('w/shippingAccounts')) {
            links.push({
                group: i18n('navGroupAdmin'),
                name: i18n('navShippingAccounts'),
                icons: ['fas fa-truck'],
                url: `${window.IAPV2Root}/shippingaccounts`,
            });
        }

        if (await HasPerm('r/users/taxExemptions')) {
            links.push({
                group: i18n('navGroupAdmin'),
                name: i18n('navTaxExemptions'),
                icons: ['fas fa-taxi'],
                url: Relref('/customers/tax-exemptions'),
            });
        }
        // ==================== SYSTEM ====================

        if (await HasPerm('w/artistTaskTypes')) {
            links.push({
                group: i18n('navGroupSystem'),
                name: i18n('navArtistTaskTypes'),
                icons: ['fas fa-paint-brush'],
                url: `${window.IAPV2Root}/artisttasktypes`,
            });
        }

        if (await HasPerm('w/nodeFlows')) {
            links.push({
                group: i18n('navGroupSystem'),
                name: i18n('navAutomations'),
                icons: ['fas fa-magic'],
                url: Relref('/automations'),
            });
        }

        if (await HasPerm('r/log')) {
            links.push({
                group: i18n('navGroupSystem'),
                name: i18n('navLog'),
                icons: ['far fa-file-alt'],
                url: Relref('/log'),
            });
        }

        if (await HasPerm('w/flags')) {
            links.push({
                group: i18n('navGroupSystem'),
                name: i18n('navFlags'),
                icons: ['fas fa-flag'],
                url: Relref('/flags'),
            });
        }

        if (await HasRole(models.AdminRoleID.ADMIN_ROLES_DEVELOPER)) {
            links.push({
                group: i18n('navGroupSystem'),
                name: i18n('navMeiliSync'),
                icons: ['fas fa-sync', 'fas fa-search'],
                url: Relref('/meili'),
            });
        }

        if (await HasRole(models.AdminRoleID.ADMIN_ROLES_DEVELOPER)) {
            links.push({
                group: i18n('navGroupSystem'),
                name: i18n('navSiteSearch'),
                icons: ['fas fa-search', 'fas fa-globe'],
                url: Relref('/sites'),
            });
        }
        // ==================== KPIS ====================

        if (await HasRole(models.AdminRoleID.ADMIN_ROLES_ARTIST)) {
            links.push({
                group: i18n('navGroupKpis'),
                name: i18n('navArtistStats'),
                icons: ['fas fa-paint-brush'],
                url: Relref('/kpis/artist-dashboard'),
            });
        }
        // ==================== SYSTEM ====================

        if (await HasPerm('w/assistant')) {
            links.push({
                group: i18n('navGroupTools'),
                name: i18n('navAssistant'),
                icons: ['fas fa-robot'],
                url: Relref('/assistant'),
            });
        }

        if (await HasPerm('r/prompts')) {
            links.push({
                group: i18n('navGroupTools'),
                name: i18n('navPrompts'),
                icons: ['fas fa-scroll'],
                url: Relref('/prompts'),
            });
        }

        if (await HasPerm('r/short')) {
            links.push({
                group: i18n('navGroupTools'),
                name: i18n('navUrlShortener'),
                icons: ['fas fa-globe'],
                url: Relref('/url-shortener'),
            });
        }

        if (await HasPerm('w/uploads')) {
            links.push({
                group: i18n('navGroupTools'),
                name: i18n('navRemoveBackground'),
                icons: ['fas fa-image', 'fas fa-eraser'],
                url: Relref('/remove-background'),
            });
        }
    } else {
        if (await HasPerm('r/groupChats')) {
            links.push({
                group: i18n('navGroupInbox'),
                name: i18n('navInbox'),
                icons: ['fas fa-inbox'],
                url: Relref('/inbox'),
                badge: 'inbox',
            });

            links.push({
                group: i18n('navGroupInbox'),
                name: i18n('navInboxClosed'),
                icons: ['fas fa-inbox', 'fas fa-check'],
                url: Relref('/inbox/closed'),
            });

            links.push({
                group: i18n('navGroupInbox'),
                name: i18n('navSent'),
                icons: ['fas fa-inbox', 'fas fa-paper-plane'],
                url: Relref('/inbox/sent'),
            });
        }

        if (await HasPerm('r/orders?orderStatus=unconfirmed')) {
            links.push({
                group: i18n('navGroupOrders'),
                name: i18n('navNewOrders'),
                icons: ['fas fa-asterisk'],
                url: Relref('/orders/unconfirmed'),
                badge: 'unconfirmed',
            });
        }

        if (await HasPerm('r/orders?orderStatus=confirmed')) {
            links.push({
                group: i18n('navGroupOrders'),
                name: i18n('navConfirmedOrders'),
                icons: ['fas fa-check-double'],
                url: Relref('/orders/confirmed'),
            });
        }

        if (await HasPerm('r/orders')) {
            links.push({
                group: i18n('navGroupOrders'),
                name: i18n('navAllOrders'),
                icons: ['fas fa-check-circle'],
                url: Relref('/orders'),
            });
        }

        if (await HasPerm('r/orders/shipments')) {
            links.push({
                group: i18n('navGroupOrders'),
                name: i18n('navLateOrders'),
                icons: ['fas fa-clock', 'fas fa-exclamation'],
                url: Relref('/orders/late'),
                badge: 'late',
            });
        }

        if (await HasPerm('r/orders/shipments') && await CanReadOrderExceptions()) {
            links.push({
                group: i18n('navGroupOrders'),
                name: i18n('navLateRisk'),
                icons: ['fas fa-clock', 'fas fa-exclamation-triangle'],
                url: Relref('/orders/late-risk'),
            });
        }

        if (await HasPerm('r/orders/shipments')) {
            links.push({
                group: i18n('navGroupOrders'),
                name: i18n('navUnshippedOrders'),
                icons: ['fas fa-plane'],
                url: Relref('/orders/unshipped'),
                badge: 'unshipped',
            });
        }

        if (await HasPerm('r/shippedOrders')) {
            links.push({
                group: i18n('navGroupOrders'),
                name: i18n('navShippedOrders'),
                icons: ['fas fa-check'],
                url: Relref('/orders/shipped'),
            });
        }

        if (await HasPerm('r/cancelledOrders')) {
            links.push({
                group: i18n('navGroupOrders'),
                name: i18n('navCanceledOrders'),
                icons: ['fas fa-store-slash'],
                url: Relref('/orders/canceled'),
            });
        }

        if (await HasPerm('r/blanks')) {
            links.push({
                group: i18n('navGroupManufacturing'),
                name: i18n('navBlanks'),
                icons: ['fas fa-coins'],
                url: Relref('/blanks'),
            });
        }

        if (await HasPerm('w/userOrderBids')) {
            links.push({
                group: i18n('navGroupFactories'),
                name: i18n('navBidsNeeded'),
                icons: ['fas fa-shopping-cart'],
                url: Relref('/orders/bids-needed'),
                badge: 'ordersBidsNeeded',
            });
        }

        if (await HasPerm('r/userOrderBids')) {
            links.push({
                group: i18n('navGroupFactories'),
                name: i18n('navBiddingHistory'),
                icons: ['fas fa-history'],
                url: Relref('/orders/bids'),
            });
        }

        if (await HasRole(models.AdminRoleID.ADMIN_ROLES_SMG_PACKING_AND_SHIPPING) && await HasPerm('w/orders/shipments')) {
            links.push({
                group: i18n('navGroupShipping'),
                name: i18n('navShippedToBuilding'),
                icons: ['fas fa-bomb'],
                url: Relref('/orders/shipped-to-building'),
            });
        }

        if (await HasPerm('r/orders/shipments')) {
            links.push({
                group: i18n('navGroupShipping'),
                name: i18n('navShipments'),
                icons: ['fas fa-truck'],
                url: Relref('/shipments'),
            });
        }

        if (await HasPerm('r/orders/shipments')) {
            links.push({
                group: i18n('navGroupOrders'),
                name: i18n('navLateHistory'),
                icons: ['fas fa-clock', 'fas fa-dollar-sign'],
                url: Relref('/orders/late-history'),
            });
        }

        if (await HasPerm('r/uploads') || await HasPerm('r/images')) {
            links.push({
                group: i18n('navGroupMarketing'),
                name: i18n('navImageSearch'),
                icons: ['fas fa-photo-video', 'fas fa-search'],
                url: Relref('/search/images'),
            });
        }
    }

    return links;
}